import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLeaveSummaryApi } from "../../app/api";
import { toast } from "react-toastify";
import {
  CalendarDays,
  Clock,
  XCircle,
  CheckCircle2,
  AlertCircle,
  PieChart,
  MessageSquare
} from "lucide-react";

export default function UserLeavesProfile() {
  const { id } = useParams();

  const [balances, setBalances] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLeaves = async () => {
    try {
      setLoading(true);
      const res = await getLeaveSummaryApi(id);

      if (res?.success) {
        setLeaves(res.data.leaves || []);
        setBalances(res.data.balances || []);
      }
    } catch (err) {
      toast.error("Failed to load leave data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadLeaves();
  }, [id]);

  const getStatusBadge = (status) => {
    if (status === 1)
      return (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-widest">
          <CheckCircle2 className="w-3 h-3" />
          Approved
        </div>
      );
 
    if (status === 0 || status === 2)
      return (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-widest">
          <XCircle className="w-3 h-3" />
          Rejected
        </div>
      );
      
    if (status === 3)
      return (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-500/10 border border-gray-500/20 text-gray-400 text-[10px] font-black uppercase tracking-widest">
          <XCircle className="w-3 h-3" />
          Cancelled
        </div>
      );
 
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-[10px] font-black uppercase tracking-widest">
        <Clock className="w-3 h-3" />
        Pending
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#12082F] min-h-[500px] text-white rounded-[30px] border border-white/10 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)]">
      
      {/* HEADER */}
      <div className="flex items-center gap-4 mb-10">
        <div className="p-3.5 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shadow-purple-500/20">
          <CalendarDays className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Employee Leaves</h1>
          <p className="text-white/60 text-sm mt-1">Leave balances and history records</p>
        </div>
      </div>

      {/* BALANCES */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4 ml-1">
          <PieChart className="w-4 h-4 text-blue-400" />
          <span className="text-[11px] font-black uppercase tracking-[1px] text-white/60">Available Allowances</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {balances
            .filter((item) => ["Casual", "Work From Home", "Short Leave", "Marriage"].includes(item.leave_type))
            .map((item) => (
              <div
                key={item.leave_type_id}
                className="group relative overflow-hidden p-5 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl transition-all hover:bg-white/[0.08]"
              >
                <p className="text-[11px] font-semibold uppercase whitespace-nowrap text-white/60 mb-2">
                  {item.leave_type}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black text-white">{item.remaining_days ?? 0}</span>
                  <span className="text-[10px] font-bold text-white/40 uppercase">Left</span>
                </div>
                <div className="mt-4 pt-4 border-t border-white/5 flex justify-between items-center">
                  <span className="text-[9px] font-bold text-white/30 uppercase tracking-tighter">Usage</span>
                  <span className="text-[10px] font-semibold text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-full">
                    {item.used_days ?? 0} Used
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* HISTORY */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4 px-1">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-purple-400" />
            <span className="text-[11px] font-black uppercase tracking-[1px] text-white/60">Application History</span>
          </div>
          <span className="text-[10px] font-bold text-white/20 uppercase">{leaves.length} Applications</span>
        </div>

        {leaves.length > 0 ? (
          leaves.map((leave) => (
            <div
              key={leave.id}
              className="group relative overflow-hidden rounded-[24px] p-5 lg:p-6 bg-gradient-to-br from-[#1e174a]/60 to-[#0f0c29]/80 backdrop-blur-xl border border-white/10 transition-all hover:border-white/20"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-[15px] text-white">
                      {leave.leave_type === "Unpaid Leave" ? "Leave Without Pay [LWP]" : leave.leave_type}
                    </h3>
                    {leave.duration === 0.5 && (
                      <span className="text-[9px] font-black bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full uppercase">
                        Half
                      </span>
                    )}
                  </div>
                </div>
                <div>{getStatusBadge(leave.approval_status)}</div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-white/70 mt-4 mb-4">
                <p><span className="text-white/40 text-xs">Start:</span> {formatDate(leave.leave_start_date)}</p>
                <p><span className="text-white/40 text-xs">End:</span> {formatDate(leave.leave_end_date)}</p>
                <p><span className="text-white/40 text-xs">Duration:</span> {leave.duration} days</p>
              </div>

              {leave.employee_comment && (
                <div className="mt-4 pt-4 border-t border-white/5 flex gap-3 items-start">
                  <MessageSquare className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Employee Reason</p>
                    <p className="text-xs italic text-white/60 leading-relaxed">"{leave.employee_comment}"</p>
                  </div>
                </div>
              )}
              
              {leave.admin_comment && (
                <div className="mt-3 flex gap-3 items-start">
                  <MessageSquare className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/30">Admin Response</p>
                    <p className="text-xs italic text-purple-200/80 leading-relaxed">"{leave.admin_comment}"</p>
                  </div>
                </div>
              )}

              {leave.remarks && (
                <div className="mt-3 flex gap-3 items-start">
                  <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/30">System Remarks</p>
                    <p className="text-xs italic text-amber-200/80 leading-relaxed">"{leave.remarks}"</p>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center p-16 rounded-[30px] bg-white/5 border border-white/10 border-dashed">
            <AlertCircle className="w-10 h-10 text-white/10 mb-3" />
            <p className="text-white/30 font-bold uppercase tracking-widest text-sm">No leave records found</p>
          </div>
        )}
      </div>
    </div>
  );
}
